import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { buildAlbumParam } from "@/lib/slugify";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { ProfileActivity } from "@/components/profile/ProfileActivity";
import { ProfileStats } from "@/components/profile/ProfileStats";
import { CollectionRating } from "@/components/profile/CollectionRating";
import { FollowedArtists } from "@/components/artists/FollowedArtists";

import { AppShell } from "@/components/AppShell";

import { AchievementsGrid } from "@/components/AchievementsGrid";
import { ProBadge } from "@/components/ui/pro-badge";
import { FramedAvatar } from "@/components/FramedAvatar";
import { ProfileHeaderSkeleton, AlbumGridSkeleton } from "@/components/ui/skeletons";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import {
  getProfileByUsername,
  getProfileAlbums,
  isFollowing,
  followUser,
  unfollowUser,
  listFollowers,
  listFollowing,
} from "@/lib/social.functions";
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
} from "@/components/ui/responsive-dialog";
import { startConversation } from "@/lib/chat.functions";
import { openChatWindow } from "@/components/chat/ChatDock";
import { MapPin, MessageCircle, UserPlus, UserMinus, Disc3, Heart, Tag, Repeat2, Search, LayoutGrid, List, Calendar, ArrowUpRight, Pencil, Lock } from "lucide-react";
import { toast } from "sonner";
import { EmptyState } from "@/components/ui/empty-state";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import { TrackPreviewList } from "@/components/TrackPreviewList";

type ProfileAlbum = {
  id: string;
  discogs_id?: string | null;
  title: string;
  artist: string;
  cover_url: string | null;
  year?: number | null;
  price_brl?: number | null;
};

export const Route = createFileRoute("/profile/$username")({
  head: ({ params }) => ({
    meta: [
      { title: `@${params.username} — Coleção de vinil no Viny Club` },
      {
        name: "description",
        content: `Veja a coleção de discos, prensagens, álbuns à venda e para troca de @${params.username} no Viny Club.`,
      },
      { property: "og:title", content: `@${params.username} no Viny Club` },
      {
        property: "og:description",
        content: `Coleção de vinil, wishlist e itens para troca de @${params.username}.`,
      },
      { property: "og:type", content: "profile" },
      { property: "og:url", content: `https://viny.club/profile/${params.username}` },
    ],
    links: [
      { rel: "canonical", href: `https://viny.club/profile/${params.username}` },
    ],
  }),
  component: Profile,
  notFoundComponent: () => (
    <AppShell>
      <div className="mx-auto max-w-md px-4 py-24 text-center">
        <h1 className="font-display text-2xl font-semibold tracking-tight">
          Perfil não encontrado
        </h1>
      </div>
    </AppShell>
  ),
  errorComponent: () => (
    <AppShell>
      <div className="mx-auto max-w-md px-4 py-24 text-center">
        <h1 className="font-display text-2xl font-semibold tracking-tight">
          Erro ao carregar perfil
        </h1>
      </div>
    </AppShell>
  ),
});

type Tab = "collection" | "wishlist" | "marketplace" | "stats" | "activity" | "achievements";

function ProfileBio({ bio }: { bio: string }) {
  const [expanded, setExpanded] = useState(false);
  const isLong = bio.length > 180;
  const shown = !isLong || expanded ? bio : bio.slice(0, 180).trimEnd() + "…";
  return (
    <div className="mt-3 max-w-2xl">
      <p className="whitespace-pre-wrap text-[15px] leading-relaxed text-foreground/85">
        {shown}
      </p>
      {isLong && (
        <button
          onClick={() => setExpanded((v) => !v)}
          className="mt-1 text-[13px] font-semibold text-muted-foreground hover:text-foreground"
        >
          {expanded ? "Ver menos" : "Ver mais"}
        </button>
      )}
    </div>
  );
}

function Profile() {
  const { username } = Route.useParams();
  const navigate = useNavigate();
  const { user: me } = useCurrentUser();
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>("collection");
  const [search, setSearch] = useState("");
  const isMobile = useIsMobile();
  const [sheetAlbum, setSheetAlbum] = useState<ProfileAlbum | null>(null);
  const [view, setView] = useState<"grid" | "list">(() => {
    if (typeof window === "undefined") return "grid";
    return window.localStorage.getItem("viny:profile-view") === "list" ? "list" : "grid";
  });
  useEffect(() => {
    try {
      window.localStorage.setItem("viny:profile-view", view);
    } catch {
      /* ignore */
    }
  }, [view]);
  const filterItems = <T extends { title: string; artist: string }>(items: T[]) => {
    const term = search.trim().toLowerCase();
    if (!term) return items;
    return items.filter(
      (a) =>
        a.title.toLowerCase().includes(term) || a.artist.toLowerCase().includes(term),
    );
  };

  const getProfile = useServerFn(getProfileByUsername);
  const getAlbums = useServerFn(getProfileAlbums);
  const isFollowingFn = useServerFn(isFollowing);
  const followFn = useServerFn(followUser);
  const unfollowFn = useServerFn(unfollowUser);
  const startConvFn = useServerFn(startConversation);
  
  const listFollowersFn = useServerFn(listFollowers);
  const listFollowingFn = useServerFn(listFollowing);
  const [followDrawer, setFollowDrawer] = useState<null | "followers" | "following">(null);

  const profileQ = useQuery({
    queryKey: ["profile", username],
    queryFn: () => getProfile({ data: { username } }),
    retry: 2,
    staleTime: 60_000,
  });

  const profile = profileQ.data;
  const isMe = me?.id === profile?.id;

  const albumsQ = useQuery({
    queryKey: ["profile-albums", profile?.id, isMe],
    enabled: !!profile?.id,
    queryFn: async () => {
      if (isMe) {
        const { supabase } = await import("@/integrations/supabase/client");
        const { data, error } = await supabase
          .from("user_albums")
          .select("id, discogs_id, title, artist, year, cover_url, genres, status, listing, price_brl, sale_price, condition")
          .eq("user_id", profile!.id)
          .order("created_at", { ascending: false });
        if (error) throw new Error(error.message);
        return data ?? [];
      }
      return getAlbums({ data: { userId: profile!.id } });
    },
  });
  const albums = albumsQ.data ?? [];
  const owned = albums.filter((a: any) => a.status === "owned");
  const wishlist = albums.filter((a: any) => a.status === "wishlist");
  const marketplace = albums.filter(
    (a: any) => a.listing === "for_sale" || a.listing === "for_trade",
  );

  const followingQ = useQuery({
    queryKey: ["isFollowing", profile?.id],
    enabled: !!profile?.id && !!me && !isMe,
    queryFn: () => isFollowingFn({ data: { targetId: profile!.id } }),
  });

  const toggleFollow = useMutation({
    mutationFn: async () => {
      if (followingQ.data?.following) return unfollowFn({ data: { targetId: profile!.id } });
      return followFn({ data: { targetId: profile!.id } });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["isFollowing", profile!.id] });
      qc.invalidateQueries({ queryKey: ["profile", username] });
      qc.invalidateQueries({ queryKey: ["feed"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const startChat = useMutation({
    mutationFn: () => startConvFn({ data: { otherUserId: profile!.id } }),
    onSuccess: ({ conversationId }) => {
      const isSmall = typeof window !== "undefined" && window.innerWidth < 1024;
      if (isSmall) {
        navigate({ to: "/chat", search: { c: conversationId } });
        return;
      }
      openChatWindow(conversationId, {
        id: profile!.id,
        username: profile!.username,
        display_name: profile!.display_name,
        avatar_url: profile!.avatar_url,
      });
    },
    onError: (e: Error) => toast.error(e.message),
  });


  if (profileQ.isLoading) {
    return (
      <AppShell>
        <ProfileHeaderSkeleton />
        <div className="mx-auto max-w-5xl px-4 pb-20">
          <AlbumGridSkeleton count={12} />
        </div>
      </AppShell>
    );
  }
  if (profileQ.isError) {
    return (
      <AppShell>
        <div className="mx-auto max-w-md px-4 py-24 text-center">
          <h1 className="font-display text-2xl font-semibold tracking-tight">
            Não foi possível carregar este perfil
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            A conexão falhou por um instante. Tente novamente.
          </p>
          <button
            type="button"
            onClick={() => profileQ.refetch()}
            className="mt-6 inline-flex items-center justify-center rounded-full bg-foreground px-5 py-2.5 text-sm font-semibold text-background"
          >
            Tentar de novo
          </button>
        </div>
      </AppShell>
    );
  }
  if (!profile) {
    return (
      <AppShell>
        <div className="mx-auto max-w-md px-4 py-24 text-center">
          <h1 className="font-display text-2xl font-semibold tracking-tight">
            Perfil não encontrado
          </h1>
        </div>
      </AppShell>
    );
  }


  const displayName = profile.display_name || profile.username;
  const avatar =
    profile.avatar_url ||
    `https://api.dicebear.com/7.x/initials/svg?seed=${profile.username}`;
  const followingNow = followingQ.data?.following;

  const tabs: Array<{ key: Tab; label: string; count?: number; locked?: boolean }> = [
    { key: "collection", label: "Coleção", count: profile.counts.owned },
    { key: "wishlist", label: "Interesse", count: profile.counts.wishlist },
    {
      key: "marketplace",
      label: "Marketplace",
      count: profile.counts.for_sale + profile.counts.for_trade,
    },
    { key: "stats", label: "Estatísticas", locked: !profile.is_premium },
    { key: "activity", label: "Atividade" },
    { key: "achievements", label: "Conquistas" },
  ];

  const coverAlbums = owned.slice(0, 6);

  return (
    <AppShell>
      {/* Cabeçalho editorial: capas ao fundo, identidade em destaque */}
      <section className="relative isolate overflow-hidden">
        {/* Fundo com as capas da coleção */}
        <div aria-hidden className="absolute inset-0 -z-10">
          {coverAlbums.length > 0 ? (
            <div className="absolute inset-0 grid grid-cols-3 sm:grid-cols-6">
              {Array.from({ length: 6 }).map((_, i) => {
                const a = coverAlbums[i % coverAlbums.length];
                return (
                  <div key={i} className={cn("relative overflow-hidden", i >= 3 && "hidden sm:block")}>
                    {a?.cover_url ? (
                      <img
                        src={a.cover_url}
                        alt=""
                        loading="lazy"
                        decoding="async"
                        className="h-full w-full scale-125 object-cover opacity-70 blur-2xl"
                      />
                    ) : (
                      <div className="h-full w-full bg-surface/60" />
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-primary/25 via-surface/30 to-transparent" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/85 to-background/50" />
          {profile.is_premium && (
            <div className="absolute inset-0 bg-gradient-to-tr from-primary/20 via-transparent to-transparent" />
          )}
        </div>

        <div className="mx-auto max-w-5xl px-4 pb-6 pt-8 sm:px-6 sm:pb-8 sm:pt-14">
          <div className="flex flex-col gap-5 sm:flex-row sm:items-end sm:gap-7">
            <div className="shrink-0">
              <FramedAvatar
                userId={profile.id}
                src={profile.avatar_url}
                username={profile.username}
                isPremium={profile.is_premium}
                size={isMobile ? 96 : 144}
              />
            </div>

            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
                <h1 className="font-display text-[30px] font-semibold leading-[1.05] tracking-[-0.03em] sm:text-[44px]">
                  {displayName}
                </h1>
                {profile.is_premium && <ProBadge size="xs" aria-label="Premium" />}
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px] text-muted-foreground">
                <span>@{profile.username}</span>
                {(profile.city || profile.country) && (
                  <span className="flex items-center gap-1">
                    <MapPin className="h-3.5 w-3.5" strokeWidth={1.6} />
                    {[profile.city, profile.country].filter(Boolean).join(", ")}
                  </span>
                )}
                {profile.is_premium && (
                  <span className="inline-flex items-center rounded-full border border-primary/30 bg-primary/5 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-primary">
                    Embaixador(a)
                  </span>
                )}
              </div>

              {/* Números em linha */}
              <div className="mt-4 flex flex-wrap items-baseline gap-x-5 gap-y-2 text-[13px]">
                {([
                  ["discos", profile.counts.owned, null],
                  ["seguidores", profile.counts.followers, "followers"],
                  ["seguindo", profile.counts.following, "following"],
                ] as const).map(([label, value, key]) => {
                  const clickable = key !== null;
                  return clickable ? (
                    <button
                      key={label}
                      type="button"
                      onClick={() => setFollowDrawer(key as "followers" | "following")}
                      className="inline-flex items-baseline gap-1.5 transition-opacity hover:opacity-70 active:opacity-60"
                    >
                      <span className="font-display text-[17px] font-semibold tabular-nums">{value}</span>
                      <span className="text-muted-foreground">{label}</span>
                    </button>
                  ) : (
                    <span key={label} className="inline-flex items-baseline gap-1.5">
                      <span className="font-display text-[17px] font-semibold tabular-nums">{value}</span>
                      <span className="text-muted-foreground">{label}</span>
                    </span>
                  );
                })}
              </div>
            </div>

            {/* Ações */}
            <div className="flex shrink-0 items-center gap-2 sm:pb-1">
              {isMe ? (
                <Link
                  to="/settings/profile"
                  className="inline-flex flex-1 items-center justify-center gap-2 rounded-full border border-[color:var(--hairline)] bg-surface/60 px-5 py-2.5 text-sm font-semibold transition-colors hover:bg-surface sm:flex-none"
                >
                  <Pencil className="h-3.5 w-3.5" /> Editar perfil
                </Link>
              ) : (
                <>
                  <button
                    onClick={() => (me ? toggleFollow.mutate() : navigate({ to: "/auth" }))}
                    disabled={toggleFollow.isPending}
                    className={cn(
                      "inline-flex flex-1 items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-all sm:flex-none",
                      followingNow
                        ? "border border-[color:var(--hairline)] bg-surface/60 hover:bg-surface"
                        : "bg-foreground text-background hover:opacity-90",
                    )}
                  >
                    {followingNow ? <UserMinus className="h-4 w-4" /> : <UserPlus className="h-4 w-4" />}
                    {followingNow ? "Seguindo" : "Seguir"}
                  </button>
                  <button
                    onClick={() => (me ? startChat.mutate() : navigate({ to: "/auth" }))}
                    aria-label="Mensagem"
                    className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-[color:var(--hairline)] bg-surface/60 transition-colors hover:bg-surface active:bg-surface"
                  >
                    <MessageCircle className="h-4 w-4" />
                  </button>
                </>
              )}
            </div>
          </div>

          {profile.bio && <ProfileBio bio={profile.bio} />}

          <CollectionRating ownerId={profile.id} meId={me?.id ?? null} isMe={isMe} />

          {profile.favorite_genres.length > 0 && (
            <div className="mt-3.5 flex flex-wrap gap-1.5">
              {profile.favorite_genres.map((g) => (
                <span
                  key={g}
                  className="rounded-full border border-[color:var(--hairline)] bg-surface/40 px-2.5 py-0.5 text-[11px] font-medium text-muted-foreground"
                >
                  {g}
                </span>
              ))}
            </div>
          )}
        </div>
      </section>




      <FollowDrawer
        open={followDrawer !== null}
        onOpenChange={(v) => !v && setFollowDrawer(null)}
        kind={followDrawer}
        userId={profile.id}
        fetchFollowers={listFollowersFn}
        fetchFollowing={listFollowingFn}
      />


      {/* Tabs — chips com badge de contagem */}
      <div className="sticky top-12 z-30 border-y border-foreground/10 bg-background/85 backdrop-blur-xl lg:top-[84px] lg:border-y-0 lg:bg-background/60">
        <div className="scroll-x mx-auto max-w-5xl px-4 [scroll-padding-inline:1rem] sm:px-8">
          <div className="flex min-w-max snap-x snap-mandatory gap-2.5 py-3">
            {tabs.map((t) => {
              const active = tab === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => {
                    setTab(t.key);
                  }}
                  className={cn(
                    "inline-flex snap-start items-center gap-2 rounded-full border px-4 py-2 text-[13px] font-medium tracking-tight transition-all active:scale-[0.97] sm:py-1.5",
                    active
                      ? "border-foreground bg-foreground text-background"
                      : "border-[color:var(--hairline)] bg-surface/40 text-muted-foreground hover:border-foreground/30 hover:text-foreground",
                  )}
                >
                  {t.locked && <Lock className="h-3 w-3 opacity-70" />}
                  {t.label}
                  {t.count != null && (
                    <span
                      className={cn(
                        "inline-flex min-w-[1.25rem] items-center justify-center rounded-full px-1.5 py-px text-[10px] font-semibold tabular-nums",
                        active
                          ? "bg-background/15 text-background"
                          : "bg-foreground/10 text-foreground/80",
                      )}
                    >
                      {t.count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>


      {/* Tab content */}
      <div className="mx-auto max-w-5xl px-4 pb-24 pt-5 sm:px-8 sm:pt-8">
        {albumsQ.isLoading && tab !== "achievements" && tab !== "activity" && (
          <AlbumGridSkeleton count={12} />
        )}

        {tab !== "achievements" && tab !== "activity" && tab !== "stats" && !albumsQ.isLoading && (
          <div className="mb-5 flex items-center gap-2.5">
            <div className="relative flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar por álbum ou artista"
                className="h-11 w-full rounded-full sm:h-10 border border-[color:var(--hairline)] bg-surface/40 pl-9 pr-3 text-[14px] outline-none placeholder:text-muted-foreground focus:border-foreground/30"
              />
            </div>
            <div className="flex shrink-0 items-center gap-1 rounded-full border border-[color:var(--hairline)] bg-surface/40 p-1">
              {([
                ["grid", LayoutGrid, "Grade"],
                ["list", List, "Lista"],
              ] as const).map(([mode, Icon, label]) => (
                <button
                  key={mode}
                  type="button"
                  aria-label={label}
                  aria-pressed={view === mode}
                  onClick={() => setView(mode)}
                  className={cn(
                    "inline-flex h-9 w-9 items-center justify-center rounded-full transition-colors sm:h-8 sm:w-8",
                    view === mode
                      ? "bg-foreground text-background"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  <Icon className="h-4 w-4" />
                </button>
              ))}
            </div>
          </div>
        )}

        {tab === "collection" && !albumsQ.isLoading && (
          <>
            <AlbumGrid
              items={filterItems(owned)}
              emptyTab="collection"
              view={view}
              onOpen={isMobile ? setSheetAlbum : undefined}
            />
          </>
        )}
        {tab === "wishlist" && !albumsQ.isLoading && (
          <>
            <AlbumGrid
              items={filterItems(wishlist)}
              emptyTab="wishlist"
              view={view}
              onOpen={isMobile ? setSheetAlbum : undefined}
            />
          </>
        )}
        {tab === "marketplace" && !albumsQ.isLoading && (
          <AlbumGrid
            items={filterItems(marketplace)}
            emptyTab="marketplace"
            showPrice
            view={view}
            onOpen={isMobile ? setSheetAlbum : undefined}
          />
        )}
        {tab === "stats" && (
          <ProfileStats
            items={owned}
            unlocked={profile.is_premium}
            isMe={isMe}
            displayName={displayName}
          />
        )}
        {tab === "activity" && <ProfileActivity username={profile.username} />}
        {tab === "achievements" && <AchievementsGrid userId={profile.id} />}
        {isMe && tab === "collection" && <FollowedArtists />}
      </div>

      <AlbumSheet album={sheetAlbum} onOpenChange={(v) => !v && setSheetAlbum(null)} />

    </AppShell>
  );
}

function AlbumGrid({
  items,
  emptyTab,
  showPrice,
  view = "grid",
  onOpen,
}: {
  items: ProfileAlbum[];
  emptyTab: "collection" | "wishlist" | "marketplace";
  showPrice?: boolean;
  view?: "grid" | "list";
  /** Quando definido (mobile), abre o bottom sheet em vez de navegar. */
  onOpen?: (album: ProfileAlbum) => void;
}) {
  if (items.length === 0) {
    const meta = {
      collection: {
        title: "Coleção ainda vazia",
        description: "Quando esta pessoa adicionar discos à coleção, eles aparecem por aqui.",
        icons: [<Disc3 key="d" />, <Heart key="h" />, <Tag key="t" />],
      },
      wishlist: {
        title: "Nenhum desejo por enquanto",
        description: "Os discos marcados como interesse ficam guardados aqui.",
        icons: [<Heart key="h" />, <Disc3 key="d" />, <Tag key="t" />],
      },
      marketplace: {
        title: "Sem anúncios no momento",
        description: "Esta pessoa ainda não colocou nada à venda ou para troca.",
        icons: [<Tag key="t" />, <Repeat2 key="r" />, <Disc3 key="d" />],
      },
    }[emptyTab];
    return (
      <div className="mx-auto max-w-xl">
        <EmptyState {...meta} />
      </div>
    );
  }

  const itemProps = (a: ProfileAlbum, className: string) =>
    onOpen
      ? ({ as: "button" as const, type: "button" as const, onClick: () => onOpen(a), className })
      : ({
          as: "link" as const,
          to: "/album/$id",
          params: { id: buildAlbumParam(String(a.discogs_id ?? a.id), a.artist, a.title) },
          className,
        });

  const Item = ({ a, className, children }: { a: ProfileAlbum; className: string; children: React.ReactNode }) => {
    const p = itemProps(a, className);
    if (p.as === "button") {
      return (
        <button type="button" onClick={p.onClick} className={p.className}>
          {children}
        </button>
      );
    }
    return (
      <Link to="/album/$id" params={p.params!} className={p.className}>
        {children}
      </Link>
    );
  };

  if (view === "list") {
    return (
      <div className="flex flex-col gap-1">
        {items.map((a) => (
          <Item
            key={a.id}
            a={a}
            className="flex w-full items-center gap-2 rounded-md px-1 py-1 text-left text-[14px] leading-tight transition-colors active:bg-surface/60 hover:bg-surface/60"
          >
            <img
              src={a.cover_url ?? ""}
              alt={a.title}
              loading="lazy"
              decoding="async"
              className="h-7 w-7 shrink-0 rounded-[3px] object-cover ring-1 ring-[color:var(--hairline)]"
            />
            <span className="min-w-0 flex-1 truncate">{a.title}</span>
            <span className="shrink-0 tabular-nums text-muted-foreground">
              {showPrice && a.price_brl != null ? `R$ ${a.price_brl}` : (a.year ?? "—")}
            </span>
          </Item>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 sm:gap-4 lg:grid-cols-5">
      {items.map((a) => (
        <Item key={a.id} a={a} className="group block w-full text-left">
          <div className="relative aspect-square overflow-hidden rounded-2xl bg-surface ring-1 ring-[color:var(--hairline)] transition-transform duration-200 active:scale-[0.97] sm:rounded-xl">
            <img
              src={a.cover_url ?? ""}
              alt={a.title}
              loading="lazy"
              decoding="async"
              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
            />
            {showPrice && a.price_brl != null && (
              <div className="absolute right-2 top-2 rounded-full bg-background/80 px-2 py-0.5 text-[11px] font-semibold backdrop-blur-sm">
                R$ {a.price_brl}
              </div>
            )}
          </div>
          <div className="mt-2 truncate text-[13px] font-medium tracking-tight">
            {a.title}
          </div>
          <div className="truncate text-[11px] text-muted-foreground">{a.artist}</div>
        </Item>
      ))}
    </div>
  );
}

function AlbumSheet({
  album,
  onOpenChange,
}: {
  album: ProfileAlbum | null;
  onOpenChange: (v: boolean) => void;
}) {
  return (
    <ResponsiveDialog open={!!album} onOpenChange={onOpenChange}>
      <ResponsiveDialogContent className="max-w-lg" mobileClassName="max-h-[90dvh]">
        {album && (
          <div className="flex max-h-[90dvh] flex-col overflow-hidden">
            <div className="flex gap-4 px-5 pt-4 sm:px-6 sm:pt-6">
              <div className="h-24 w-24 shrink-0 overflow-hidden rounded-xl bg-surface ring-1 ring-[color:var(--hairline)]">
                {album.cover_url ? (
                  <img
                    src={album.cover_url}
                    alt={`${album.title} — ${album.artist}`}
                    loading="lazy"
                    decoding="async"
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="grid h-full w-full place-items-center text-muted-foreground">
                    <Disc3 className="h-8 w-8" />
                  </div>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <ResponsiveDialogHeader className="p-0 text-left">
                  <ResponsiveDialogTitle className="line-clamp-2 text-left text-[17px] leading-snug">
                    {album.title}
                  </ResponsiveDialogTitle>
                </ResponsiveDialogHeader>
                <div className="mt-1 truncate text-[13px] text-muted-foreground">
                  {album.artist}
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[11px]">
                  {album.year && (
                    <span className="inline-flex items-center gap-1 rounded-full border border-[color:var(--hairline)] bg-surface/50 px-2 py-0.5 text-muted-foreground">
                      <Calendar className="h-3 w-3" /> {album.year}
                    </span>
                  )}
                  {album.price_brl != null && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-foreground px-2 py-0.5 font-semibold text-background">
                      <Tag className="h-3 w-3" /> R$ {album.price_brl}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-3 overflow-y-auto px-5 pb-4 sm:px-6">
              <TrackPreviewList artist={album.artist} title={album.title} />
            </div>

            <div className="border-t border-[color:var(--hairline)] p-4 sm:px-6">
              <Link
                to="/album/$id"
                params={{
                  id: buildAlbumParam(
                    String(album.discogs_id ?? album.id),
                    album.artist,
                    album.title,
                  ),
                }}
                onClick={() => onOpenChange(false)}
                className="flex w-full items-center justify-center gap-2 rounded-full bg-foreground px-5 py-3 text-sm font-semibold text-background active:opacity-90"
              >
                Ver detalhes do álbum <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        )}
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  );
}

function FollowDrawer({
  open,
  onOpenChange,
  kind,
  userId,
  fetchFollowers,
  fetchFollowing,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  kind: "followers" | "following" | null;
  userId: string;
  fetchFollowers: (args: { data: { userId: string } }) => Promise<any>;
  fetchFollowing: (args: { data: { userId: string } }) => Promise<any>;
}) {
  const q = useQuery({
    queryKey: ["follow-list", kind, userId],
    enabled: open && !!kind,
    queryFn: () =>
      kind === "followers"
        ? fetchFollowers({ data: { userId } })
        : fetchFollowing({ data: { userId } }),
  });
  const people: Array<{
    id: string;
    username: string;
    display_name: string | null;
    avatar_url: string | null;
    is_premium: boolean;
  }> = q.data ?? [];

  return (
    <ResponsiveDialog open={open} onOpenChange={onOpenChange}>
      <ResponsiveDialogContent className="max-w-md sm:max-h-[80vh]">
        <ResponsiveDialogHeader className="px-5 pt-4 sm:px-6 sm:pt-6">
          <ResponsiveDialogTitle>
            {kind === "followers" ? "Seguidores" : "Seguindo"}
          </ResponsiveDialogTitle>
        </ResponsiveDialogHeader>
        <div className="max-h-[70vh] overflow-y-auto px-2 pb-4 sm:px-3 sm:pb-6">
          {q.isLoading ? (
            <div className="space-y-2 px-3 py-2">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="h-10 w-10 animate-pulse rounded-full bg-muted" />
                  <div className="h-3 w-32 animate-pulse rounded bg-muted" />
                </div>
              ))}
            </div>
          ) : people.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-muted-foreground">
              {kind === "followers"
                ? "Ninguém segue este perfil ainda."
                : "Não está seguindo ninguém ainda."}
            </div>
          ) : (
            <ul className="divide-y divide-[color:var(--hairline)]">
              {people.map((p) => (
                <li key={p.id}>
                  <Link
                    to="/profile/$username"
                    params={{ username: p.username }}
                    onClick={() => onOpenChange(false)}
                    className="flex items-center gap-3 rounded-md px-3 py-2.5 hover:bg-muted/50"
                  >
                    <img loading="lazy" decoding="async"
                      src={
                        p.avatar_url ||
                        `https://api.dicebear.com/7.x/initials/svg?seed=${p.username}`
                      }
                      alt={p.username}
                      className="h-10 w-10 rounded-full object-cover"
                    />
                    <div className="min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="truncate text-sm font-medium">
                        {p.display_name || p.username}
                      </span>
                      {p.is_premium && (
                        <ProBadge size="xs" aria-label="Premium" />
                      )}
                    </div>
                      <div className="truncate text-xs text-muted-foreground">
                        @{p.username}
                      </div>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  );
}

